// src/tabs/logements/AssistanceAdminEditor.jsx — PR-FIX-5c (ADR-163)
//
// Éditeur admin visuel pour la section JSONB `livret_sections_auto.content`
// (section_type='assistance').
//
// Remplace l'édition textarea JSON brute existante (SectionAutoEditModal reste
// utilisé pour les autres sections_auto et fallback dev).
//
// Layout :
//   1. "Numéros d'urgence" : 5 numéros fixes lecture seule + badge non-modifiable
//   2. "Contacts services" : 3 cards IA (Médecin/Pharmacie/Hôpital) avec
//      icône Lucide (Stethoscope/Pill/Building2) + toggle displayed_to_guest +
//      bouton "Régénérer IA" + badge état AFFICHÉ/MASQUÉ
//   3. "Contacts personnalisés" : grille cards custom_contacts triés par
//      manual_order_index, filtrés !manual_deleted. 3 boutons par card
//      (Afficher/Masquer, Modifier→AddContactModal, Supprimer confirm inline).
//      Bouton "+ Ajouter un contact" → AddContactModal mode création.
//
// Calls API (consomme PR-FIX-5c C2 backend) :
//   - livret_assistance_contact_toggle (IA et custom)
//   - livret_assistance_contact_add (création custom)
//   - livret_assistance_contact_update (édition custom)
//   - livret_assistance_contact_delete (soft delete custom)
//   - livret_sections_auto_generate type='assistance' (régénération IA)
//
// Auth : récupère JWT supabase via supabase.auth.getSession() (helper
// getAuthHeaders local, pattern AlentoursAdminEditor + LivretModulesEditor).

import { useState, useEffect, useCallback } from "react";
import { Stethoscope, Pill, Building2 } from "lucide-react";
import { DS } from "../../lib/ui";
import { supabase } from "../../supabase";
import AddContactModal from "./AddContactModal";

const URGENCY_NUMBERS = [
  { key: "police", label: "Police", number: "17" },
  { key: "samu", label: "SAMU", number: "15" },
  { key: "pompiers", label: "Pompiers", number: "18" },
  { key: "urgences_eu", label: "Urgences EU", number: "112" },
  { key: "contact_fbz", label: "FullBnBZen", number: null }, // dynamique depuis env
];

const IA_CONTACTS = [
  { key: "medecin_proche", label: "Médecin proche", Icon: Stethoscope },
  { key: "pharmacie_garde", label: "Pharmacie proche", Icon: Pill },
  { key: "hopital_proche", label: "Hôpital proche", Icon: Building2 },
];

const CONTACT_TYPE_LABELS = {
  veterinaire: "Vétérinaire",
  dentiste: "Dentiste",
  taxi: "Taxi",
  babysitter: "Babysitter",
  autre: "Autre",
};

async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

async function _apiCall(action, { method = "POST", body } = {}) {
  const url = `/api/beds24-mapping?action=${action}`;
  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({ error: "parse_fail" }));
  if (!res.ok) {
    const err = new Error(json.message || json.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.error = json.error;
    throw err;
  }
  return json;
}

export default function AssistanceAdminEditor({ logement_id, row, onReload }) {
  const content = row?.content || {};
  const customContacts = Array.isArray(content.custom_contacts)
    ? content.custom_contacts.filter((c) => c && !c.manual_deleted)
    : [];

  const [busyKeys, setBusyKeys] = useState({});
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [modalContact, setModalContact] = useState(null); // null | "create" | contact object
  const [modalIaKey, setModalIaKey] = useState(null); // null | "medecin_proche" | ... (BUG24)
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const setBusy = useCallback((id, val) => {
    setBusyKeys((prev) => {
      const next = { ...prev };
      if (val) next[id] = true;
      else delete next[id];
      return next;
    });
  }, []);

  // ── Toggle displayed_to_guest sur IA contact ──────────────────────────────
  async function handleToggleIA(key, currentValue) {
    setBusy(`ia:${key}`, true);
    setError(null);
    try {
      await _apiCall("livret_assistance_contact_toggle", {
        method: "POST",
        body: {
          logement_id,
          key,
          field: "displayed_to_guest",
          value: !currentValue,
        },
      });
      if (onReload) await onReload();
    } catch (e) {
      setError(e.message || "Erreur toggle IA");
    } finally {
      setBusy(`ia:${key}`, false);
    }
  }

  // ── Toggle displayed_to_guest sur custom contact ──────────────────────────
  async function handleToggleCustom(contact, currentValue) {
    const id = contact.manual_id;
    setBusy(`c:${id}`, true);
    setError(null);
    try {
      await _apiCall("livret_assistance_contact_toggle", {
        method: "POST",
        body: {
          logement_id,
          contact_id: id,
          field: "displayed_to_guest",
          value: !currentValue,
        },
      });
      if (onReload) await onReload();
    } catch (e) {
      setError(e.message || "Erreur toggle contact");
    } finally {
      setBusy(`c:${id}`, false);
    }
  }

  // ── Supprimer un custom contact (soft delete) ─────────────────────────────
  async function handleDelete(contact) {
    const id = contact.manual_id;
    setBusy(`del:${id}`, true);
    setError(null);
    try {
      await _apiCall("livret_assistance_contact_delete", {
        method: "POST",
        body: { logement_id, contact_id: id },
      });
      setDeleteConfirmId(null);
      if (onReload) await onReload();
    } catch (e) {
      setError(e.message || "Erreur suppression");
    } finally {
      setBusy(`del:${id}`, false);
    }
  }

  // ── Régénération IA contacts médecin/pharmacie/hôpital ────────────────────
  async function handleRegenerateIA() {
    if (
      !window.confirm(
        "Régénérer les contacts IA (médecin / pharmacie / hôpital) ?\n\n" +
          "ATTENTION : les 3 contacts IA seront ÉCRASÉS par les nouveaux " +
          "résultats Google Places — Y COMPRIS toute édition manuelle " +
          "(nom / téléphone / adresse) que vous auriez faite sur ces contacts.\n\n" +
          "Les contacts personnalisés (vétérinaire, taxi, etc.) et leurs préférences " +
          "d'affichage, eux, sont PRÉSERVÉS.",
      )
    ) {
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      await _apiCall("livret_sections_auto_generate", {
        method: "POST",
        body: { logement_id, type: "assistance" },
      });
      if (onReload) await onReload();
    } catch (e) {
      setError(e.message || "Erreur régénération");
    } finally {
      setGenerating(false);
    }
  }

  // ── Helpers state ─────────────────────────────────────────────────────────
  const fbzPhone = content.contact_fbz || "+33 7 83 31 85 06";

  return (
    <div style={{ fontFamily: DS.fontBody }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h4
            style={{
              margin: 0,
              fontFamily: DS.fontDisplay,
              color: DS.navy,
              fontSize: 16,
            }}
          >
            🚨 Édition Assistance & Urgences
          </h4>
          <p style={{ margin: "4px 0 0", fontSize: 11, color: "#666" }}>
            {customContacts.length} contact{customContacts.length > 1 ? "s" : ""}{" "}
            personnalisé{customContacts.length > 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {error && (
        <div role="alert" style={_errorBoxStyle()}>
          {error}
        </div>
      )}

      {/* Section 1 — Numéros d'urgence fixes */}
      <section style={_sectionStyle()}>
        <h5 style={_sectionTitleStyle()}>📞 Numéros d'urgence (non-modifiables)</h5>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 8,
          }}
        >
          {URGENCY_NUMBERS.map((u) => (
            <div
              key={u.key}
              style={{
                padding: "10px 12px",
                background: "#f8f9ff",
                borderRadius: 7,
                border: `1px solid ${DS.border}`,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <span style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>
                {u.label}
              </span>
              <span
                style={{
                  fontFamily: DS.fontBody,
                  fontSize: 14,
                  fontWeight: 700,
                  color: DS.navy,
                }}
              >
                {u.number || fbzPhone}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Section 2 — Contacts IA Google Places */}
      <section style={_sectionStyle()}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <h5 style={{ ..._sectionTitleStyle(), margin: 0 }}>🏥 Contacts services (IA)</h5>
          <button
            type="button"
            onClick={handleRegenerateIA}
            disabled={generating}
            style={_btnGhostStyle(DS.terra, generating)}
            title="Régénère les 3 contacts IA via Google Places. Préserve les flags admin."
          >
            {generating ? "Régénération…" : "Régénérer IA"}
          </button>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 10,
          }}
        >
          {IA_CONTACTS.map(({ key, label, Icon }) => {
            const c = content[key];
            const exists = Boolean(c && c.name);
            const displayed = exists ? c.displayed_to_guest !== false : false;
            const busy = busyKeys[`ia:${key}`];
            return (
              <article
                key={key}
                style={{
                  padding: 10,
                  background: DS.white,
                  borderRadius: 8,
                  border: `1px solid ${DS.border}`,
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: "rgba(135,184,230,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={18} color={DS.navy} aria-hidden="true" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 10,
                      color: "#888",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: 2,
                    }}
                  >
                    {label}
                  </div>
                  {exists ? (
                    <>
                      <div
                        style={{
                          fontFamily: DS.fontDisplay,
                          fontWeight: 700,
                          fontSize: 13,
                          color: DS.navy,
                          marginBottom: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {c.name}
                      </div>
                      {c.phone && (
                        <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
                          📞 {c.phone}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <button
                          type="button"
                          onClick={() => handleToggleIA(key, displayed)}
                          disabled={busy}
                          style={_btnGhostStyle(displayed ? DS.skyDeep : "#888", busy)}
                          aria-label={
                            displayed
                              ? `Masquer ${label} du livret voyageur`
                              : `Afficher ${label} dans le livret voyageur`
                          }
                          title={
                            displayed
                              ? "Cliquez pour masquer ce contact aux voyageurs"
                              : "Cliquez pour afficher ce contact aux voyageurs"
                          }
                        >
                          {displayed ? "✓ AFFICHÉ" : "○ MASQUÉ"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setModalIaKey(key)}
                          style={_btnGhostStyle(DS.navy, false)}
                          aria-label={`Modifier ${label}`}
                        >
                          ✏️ Modifier
                        </button>
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        fontSize: 11,
                        color: "#888",
                        fontStyle: "italic",
                      }}
                    >
                      Non trouvé. Régénérer pour récupérer un contact Google Places.
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Section 3 — Custom contacts CRUD libre */}
      <section style={_sectionStyle()}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <h5 style={{ ..._sectionTitleStyle(), margin: 0 }}>
            ➕ Contacts personnalisés ({customContacts.length})
          </h5>
          <button
            type="button"
            onClick={() => setModalContact("create")}
            style={_btnPrimaryStyle(false)}
          >
            ➕ Ajouter un contact
          </button>
        </div>
        {customContacts.length === 0 && (
          <p
            style={{
              fontSize: 12,
              color: "#888",
              fontStyle: "italic",
              margin: "8px 0",
            }}
          >
            Aucun contact personnalisé. Ajoutez vétérinaire, dentiste, taxi, etc.
            via le bouton ci-dessus.
          </p>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 10,
          }}
        >
          {customContacts.map((c) => {
            const displayed = c.displayed_to_guest !== false;
            const busyToggle = busyKeys[`c:${c.manual_id}`];
            const busyDel = busyKeys[`del:${c.manual_id}`];
            const typeLabel = CONTACT_TYPE_LABELS[c.type] || c.type || "Contact";
            return (
              <article
                key={c.manual_id}
                style={{
                  padding: 10,
                  background: DS.white,
                  borderRadius: 8,
                  border: `1px solid ${DS.border}`,
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    padding: "2px 8px",
                    background: "rgba(135,184,230,0.15)",
                    color: DS.navy,
                    fontSize: 10,
                    fontWeight: 700,
                    borderRadius: 999,
                    marginBottom: 6,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {typeLabel}
                </div>
                <div
                  style={{
                    fontFamily: DS.fontDisplay,
                    fontWeight: 700,
                    fontSize: 14,
                    color: DS.navy,
                    marginBottom: 2,
                  }}
                >
                  {c.name}
                </div>
                {c.phone && (
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 2 }}>
                    📞 {c.phone}
                  </div>
                )}
                {c.address && (
                  <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
                    📍 {c.address}
                  </div>
                )}
                {c.notes && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#666",
                      fontStyle: "italic",
                      marginTop: 4,
                      marginBottom: 4,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {c.notes}
                  </div>
                )}
                <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => handleToggleCustom(c, displayed)}
                    disabled={busyToggle}
                    style={_btnGhostStyle(displayed ? DS.skyDeep : "#888", busyToggle)}
                    aria-label={
                      displayed
                        ? `Masquer ${c.name} du livret voyageur`
                        : `Afficher ${c.name} dans le livret voyageur`
                    }
                  >
                    {displayed ? "✓ AFFICHÉ" : "○ MASQUÉ"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalContact(c)}
                    style={_btnGhostStyle(DS.navy, false)}
                    aria-label={`Modifier ${c.name}`}
                  >
                    ✏️ Modifier
                  </button>
                  {deleteConfirmId === c.manual_id ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleDelete(c)}
                        disabled={busyDel}
                        style={_btnGhostStyle(DS.charge, busyDel)}
                        aria-label={`Confirmer la suppression de ${c.name}`}
                      >
                        {busyDel ? "⏳" : "✓ Confirmer"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmId(null)}
                        style={_btnGhostStyle("#888", false)}
                      >
                        Annuler
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmId(c.manual_id)}
                      style={_btnGhostStyle(DS.charge, false)}
                      aria-label={`Supprimer ${c.name}`}
                    >
                      🗑️ Supprimer
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Modal Ajouter/Modifier (custom contacts) */}
      {modalContact && (
        <AddContactModal
          key={
            modalContact === "create"
              ? "create"
              : `custom:${modalContact.manual_id}`
          }
          logement_id={logement_id}
          contact={modalContact === "create" ? null : modalContact}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setModalContact(null)}
          onSaved={async () => {
            setModalContact(null);
            if (onReload) await onReload();
          }}
        />
      )}

      {/* Modal Modifier contact IA (BUG24/Option A) — montage conditionnel
          avec clé React distincte par key pour éviter le snapshot stale. */}
      {modalIaKey && (
        <AddContactModal
          key={`ia:${modalIaKey}`}
          logement_id={logement_id}
          iaKey={modalIaKey}
          contact={content[modalIaKey] || null}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setModalIaKey(null)}
          onSaved={async () => {
            setModalIaKey(null);
            if (onReload) await onReload();
          }}
        />
      )}
    </div>
  );
}

// ── Styles helpers ─────────────────────────────────────────────────────────
function _sectionStyle() {
  return {
    marginBottom: 16,
    padding: 12,
    background: "#fbfcfe",
    borderRadius: 10,
    border: `1px solid ${DS.border}`,
  };
}

function _sectionTitleStyle() {
  return {
    fontFamily: DS.fontDisplay,
    color: DS.navy,
    fontSize: 14,
    margin: "0 0 8px",
  };
}

function _btnPrimaryStyle(busy) {
  return {
    padding: "10px 16px",
    background: DS.navy,
    color: DS.white,
    border: "none",
    borderRadius: 7,
    cursor: busy ? "wait" : "pointer",
    fontFamily: DS.fontBody,
    fontSize: 12,
    fontWeight: 600,
    minHeight: 44,
    opacity: busy ? 0.6 : 1,
  };
}

function _btnGhostStyle(color, disabled) {
  return {
    padding: "8px 10px",
    background: DS.white,
    color,
    border: `1px solid ${color}`,
    borderRadius: 6,
    cursor: disabled ? "wait" : "pointer",
    fontFamily: DS.fontBody,
    fontSize: 11,
    fontWeight: 600,
    minHeight: 36,
    opacity: disabled ? 0.6 : 1,
  };
}

function _errorBoxStyle() {
  return {
    padding: 10,
    marginBottom: 12,
    background: "#fef2f2",
    border: `1px solid ${DS.charge}`,
    borderRadius: 7,
    color: DS.charge,
    fontSize: 12,
    fontFamily: DS.fontBody,
  };
}
